import { AssetRecordType, createShapeId } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import type { TLImageShape, TLImageAsset, TLShape } from "@tldraw/tlschema";
import type { Sheet, Trace } from "../shared/schema";
import { traceCrop, type Rect } from "../shared/trace-geometry";
import { editableTarget } from "./tracing";
export type ImageSurface = TLImageShape | Sheet;
export function isImageSurface(s: TLShape): s is ImageSurface {
  return s.type === "image" || (s.type === "sheet" && !!s.props.preview);
}
/** Reuse the retained raster preview of a plan; no screenshot, re-upload or PDF decode. */
export async function copyPlanArea(
  editor: Editor,
  paper: Trace,
  source: Sheet,
  region: Rect,
  signal: AbortSignal,
) {
  const src = source.props.preview.split("?")[0];
  const existing = editor
    .getAssets()
    .find((a) => a.type === "image" && a.props.src?.split("?")[0] === src);
  let asset = existing?.type === "image" ? existing : undefined;
  if (!asset) {
    const response = await fetch(src, { method: "HEAD", signal });
    const w = Number(response.headers.get("X-Image-Width")),
      h = Number(response.headers.get("X-Image-Height"));
    if (!response.ok || !w || !h || w * h > 40_000_000)
      throw new Error(
        "This plan preview is unavailable. Try again after refreshing.",
      );
    asset = AssetRecordType.create({
      id: AssetRecordType.createId(),
      type: "image",
      props: {
        src,
        w,
        h,
        name: `${source.props.name} — plan image`,
        mimeType: response.headers.get("Content-Type") || "image/png",
        isAnimated: false,
      },
      meta: { imagePreviews: true },
    }) as TLImageAsset;
  }
  signal.throwIfAborted();
  const live = editor.getShape<Sheet>(source.id),
    trace = editor.getShape<Trace>(paper.id);
  if (
    live?.type !== "sheet" ||
    live.props.preview !== source.props.preview ||
    live.props.w !== source.props.w ||
    live.props.h !== source.props.h
  )
    throw new Error("The plan changed. Select the area again.");
  if (!trace || trace.type !== "trace" || !editableTarget(editor, trace))
    throw new Error("The tracing sheet is no longer editable.");
  if (
    editor.isShapeHidden(live) ||
    !editor
      .getShapePageBounds(live)
      ?.collides(editor.getShapePageBounds(trace)!)
  )
    throw new Error("The plan or tracing sheet moved. Choose the area again.");
  const transform = editor.getShapePageTransform(live);
  const p = editor.getPointInShapeSpace(
    trace,
    transform.applyToPoint({
      x: region.x * live.props.w,
      y: region.y * live.props.h,
    }),
  );
  const w = live.props.w * region.w,
    h = live.props.h * region.h;
  const crop = traceCrop(null, region, false, false),
    id = createShapeId();
  editor.markHistoryStoppingPoint("Copy plan area onto paper");
  editor.run(() => {
    if (!existing) editor.createAssets([asset!]);
    editor.createShape<TLImageShape>({
      id,
      type: "image",
      parentId: trace.id,
      x: p.x,
      y: p.y,
      rotation:
        transform.rotation() - editor.getShapePageTransform(trace).rotation(),
      props: {
        assetId: asset!.id,
        w,
        h,
        crop,
        altText: `${source.props.name} — selected area`,
      },
      meta: {
        metersPerUnit: 0,
        calibrationWidth: w,
        calibration: "Not to Scale",
      },
    });
    editor.select(trace.id);
  });
  return id;
}
